import * as React from 'react';
import { Dispatch } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import * as copy from 'copy-to-clipboard';

import { StoreState } from '../../../../reducers';
import Popup, { FooterData, PopupTheme } from '../../../Popup';
import Select, { SelectTheme } from '../../../Form/Select';
import { PopupBody, Message } from './styles';
import { closeShareViaCopyPopup, setCopyMode } from '../../../../containers/Share/actions';
import { CopyMode, ShareViaCopyResource } from '../../../../containers/Share/reducer';
import UserAgentUtils from '../../../../utils/userAgent';
import NativeApi from '../../../../utils/nativeApi';
import { CallbackAction, runCallbackAction } from '../../../../containers/DriveItemList/actions';

interface ShareViaCopyPopupProps {
	isDisplayed: boolean;
	isProcessing: boolean;
	resourceList: ShareViaCopyResource[];
	pin: string;
	copyMode: CopyMode;
	theme: PopupTheme | undefined;
	callbackAction: CallbackAction | undefined;
	dispatch: Dispatch<StoreState>;
}

const getPlainText = (resourceList: ShareViaCopyResource[], pin: string): string => {
	let mailText = '';
	resourceList.forEach(({ url, label }) => {
		if (resourceList.length === 1) {
			mailText += `${url}\n`;
		} else {
			mailText += `${label}\n${url}\n`;
		}

		if (pin) {
			mailText += `${I18n.t('messages.pin_code')}: ${pin}\n\n`;
		}
	});
	return mailText;
};

const getHtmlText = (resourceList: ShareViaCopyResource[], pin: string): string => {
	let mailText = '<ul style="list-style-type: \'- \';">';
	resourceList.forEach(({ url, label }) => {
		mailText += `<li><a href="${url}">${label}</a>`;
		if (pin) {
			mailText += `<br>${I18n.t('messages.pin_code')}: ${pin}`;
		}
		mailText += '</li>';
	});
	mailText += '</ul>';
	return mailText;
};

const copyModeOptions = [
	{
		value: CopyMode.html,
		label: 'messages.copy_mode_html',
	},
	{
		value: CopyMode.text,
		label: 'messages.copy_mode_text',
	},
];

const copyHtmlWithPlainTextFallback = (plainText: string, htmlText: string): void => {
	copy(htmlText, {
		onCopy: (clipboardData: { setData: (format: string, data: string) => void }) => {
			clipboardData.setData('text/html', htmlText);
			clipboardData.setData('text/plain', plainText);
		},
	});
};

const ShareViaCopyPopup = ({
	isDisplayed,
	isProcessing,
	resourceList,
	pin,
	copyMode,
	theme,
	callbackAction,
	dispatch,
}: ShareViaCopyPopupProps) => {
	const popupFooter: FooterData = {
		label: I18n.t('messages.copy'),
		onAccept: () => {
			if (copyMode === CopyMode.text) {
				copy(getPlainText(resourceList, pin));
			} else {
				const plainText = getPlainText(resourceList, pin);
				const htmlText = getHtmlText(resourceList, pin);

				if (UserAgentUtils.isAndroidApp()) {
					NativeApi.copyHtmlToClipboard(plainText, htmlText);
				} else {
					copyHtmlWithPlainTextFallback(plainText, htmlText);
				}
			}

			dispatch(runCallbackAction(callbackAction));
			dispatch(closeShareViaCopyPopup());
		},
	};

	const handleChange = ({ value }) => {
		dispatch(setCopyMode(value));
	};

	return (
		<Popup
			isDisplayed={isDisplayed}
			isLoading={isProcessing}
			label={I18n.t('messages.share_via_copy')}
			onCancel={() => dispatch(closeShareViaCopyPopup())}
			footer={popupFooter}
			theme={theme}
			gallerySlot="shareForm"
		>
			<PopupBody>
				<Message
					value="messages.copy_links_to_share"
					theme={theme}
				/>
				<Select
					value={copyMode}
					options={copyModeOptions}
					theme={theme === PopupTheme.DARK ? SelectTheme.DARK : SelectTheme.LIGHT}
					onChange={handleChange}
				/>
			</PopupBody>
		</Popup>
	);
};

export default ShareViaCopyPopup;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Share/UprezForm/ShareViaCopyPopup/index.tsx